import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Label, Input, FormGroup, Button } from 'reactstrap';
import * as shipment_urls from '../../../../urls/api-shipments/merchant';

const PREFERRED_DRIVER_DISCLAIMER =
  'Selecting a preferred driver only gives that driver the opportunity to take this load first, ' +
  'if they are available. If they pass or do not respond within 5 minutes, the load is released to ' +
  'the general Gophr driver pool and is eligible for anyone with the appropriate credentials to take.';

const parseCoordinate = value => {
  const numeric = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// Optional preferred-driver picker for checkout. Lists drivers within 50 miles of pickup who are not
// already on an expedited load. Leaving it blank keeps the order on the normal flow.
const PreferredDriverSelect = ({ pickupLat, pickupLng, marketId, vehicleType, value, onChange }) => {
  const [drivers, setDrivers] = useState([]);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestTokenRef = useRef(0);

  const lat = parseCoordinate(pickupLat);
  const lng = parseCoordinate(pickupLng);
  const hasPickup = lat !== null && lng !== null;

  useEffect(() => {
    if (!hasPickup) {
      setDrivers([]);
      return;
    }

    const requestToken = requestTokenRef.current + 1;
    requestTokenRef.current = requestToken;
    setLoading(true);
    setError('');

    const params = { pickup_lat: lat, pickup_lng: lng, radius_miles: 50 };
    if (marketId) {
      params.market_id = marketId;
    }
    if (vehicleType) {
      params.vehicle_type = vehicleType;
    }

    axios
      .get(shipment_urls.preferredDrivers, {
        params,
        headers: { Authorization: 'Bearer ' + window.localStorage.getItem('access_token') },
      })
      .then(response => {
        if (requestTokenRef.current !== requestToken) {
          return;
        }
        const payload = response && response.data ? response.data.payload : null;
        setDrivers(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (requestTokenRef.current !== requestToken) {
          return;
        }
        setDrivers([]);
        setError('Could not load nearby drivers right now. You can still submit without a preferred driver.');
      })
      .finally(() => {
        if (requestTokenRef.current === requestToken) {
          setLoading(false);
        }
      });
  }, [hasPickup, lat, lng, marketId, vehicleType]);

  const filteredDrivers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return drivers;
    }
    return drivers.filter(driver => (driver.driver_name || '').toLowerCase().includes(normalizedQuery));
  }, [drivers, query]);

  const handleSelect = driver => {
    setQuery('');
    setIsOpen(false);
    onChange({
      driver_auth0_id: driver.auth0_id,
      driver_id: driver.driver_id,
      driver_name: driver.driver_name,
    });
  };

  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    onChange(null);
  };

  return (
    <FormGroup style={{ marginLeft: 20, marginRight: 20, marginBottom: 20 }}>
      <Label style={{ color: '#fff', fontWeight: 600 }}>Preferred Driver (optional)</Label>

      {value && value.driver_auth0_id ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 12px',
            border: '1px solid #344675',
            borderRadius: 8,
            backgroundColor: '#1f2133',
          }}
        >
          <span style={{ color: '#f7941d', fontWeight: 600 }}>{value.driver_name || 'Selected driver'}</span>
          <Button color="default" size="sm" onClick={handleClear}>
            Remove
          </Button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Input
            type="text"
            placeholder={hasPickup ? 'Search a driver in the area…' : 'Enter a pickup address to choose a preferred driver'}
            value={query}
            disabled={!hasPickup}
            onChange={e => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
          />
          {isOpen && hasPickup && (
            <div
              style={{
                position: 'absolute',
                zIndex: 20,
                width: '100%',
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid #344675',
                borderRadius: 8,
                backgroundColor: '#27293d',
                marginTop: 4,
              }}
            >
              {loading ? (
                <div style={{ padding: '10px 12px', color: '#cdcaca' }}>Loading nearby drivers…</div>
              ) : filteredDrivers.length === 0 ? (
                <div style={{ padding: '10px 12px', color: '#cdcaca' }}>No eligible drivers in the area.</div>
              ) : (
                filteredDrivers.map(driver => (
                  <div
                    key={driver.auth0_id}
                    onClick={() => handleSelect(driver)}
                    style={{ padding: '10px 12px', cursor: 'pointer', color: '#fff', borderBottom: '1px solid #344675' }}
                  >
                    <div style={{ fontWeight: 600 }}>{driver.driver_name}</div>
                    <div style={{ fontSize: 12, color: '#cdcaca' }}>
                      {Number.isFinite(driver.distance_miles) ? `${driver.distance_miles.toFixed(1)} mi away` : ''}
                      {driver.vehicle_name ? ` · ${driver.vehicle_name}` : ''}
                      {driver.is_online ? ' · Online' : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {error ? <p style={{ color: '#f7c56b', fontSize: 12, marginTop: 6 }}>{error}</p> : null}

      <p style={{ color: '#cdcaca', fontSize: 12, marginTop: 8 }}>{PREFERRED_DRIVER_DISCLAIMER}</p>
    </FormGroup>
  );
};

export default PreferredDriverSelect;
